import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@shared/lib';

/**
 * Variant styles use ONLY Figma token utilities (bg-primary, text-fg, …). No raw colours
 * here — restyle by changing tokens, never this file (ARCHITECTURE §9). Variants/sizes are
 * a 1:1 map of the Figma "Buttons" component set.
 */
export const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors select-none disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
  {
    variants: {
      variant: {
        /** High-emphasis indigo CTA. */
        primary: 'bg-primary text-primary-fg hover:opacity-90',
        /** Neutral grey. */
        secondary: 'bg-secondary text-secondary-fg hover:opacity-90',
        /** Low-emphasis lavender ("Browse files" / "Add expense"). */
        soft: 'bg-accent-soft text-fg hover:opacity-90',
        /** White + border ("Export" / "Back" / "Fix now" / social). */
        outline: 'border border-border bg-surface-raised text-fg hover:bg-surface',
        /** Black ("Settle up"). */
        dark: 'bg-dark text-dark-fg hover:opacity-90',
        /** Solid destructive. */
        danger: 'bg-danger text-danger-fg hover:opacity-90',
        /** Soft status pill ("Incomplete"). */
        dangerSoft: 'bg-danger-strong/10 text-danger-strong hover:bg-danger-strong/15',
        /** Muted status pill ("No group"). */
        muted: 'bg-accent-text/10 text-accent-text hover:bg-accent-text/15',
        /** Auth CTA ("Login") — violet, paired with `shape="cta"`. */
        cta: 'bg-violet-cta font-display font-semibold tracking-[-0.16px] text-white hover:opacity-90',
        /** Transparent until hovered. */
        ghost: 'text-fg hover:bg-surface',
        /** Inline text link. */
        link: 'text-link underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-9 px-3 text-sm',
        lg: 'h-13 px-6 text-base',
        xl: 'h-15 px-8 text-xl font-semibold gap-4',
        icon: 'h-9 w-9 p-0',
        'icon-sm': 'h-7 w-7 p-0',
      },
      shape: {
        rounded: '',
        pill: 'rounded-full',
        /** Figma "Login" asymmetric corners (60 / 30 / 60 / 30). */
        cta: 'rounded-tl-[60px] rounded-tr-[30px] rounded-br-[60px] rounded-bl-[30px]',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    compoundVariants: [
      { shape: 'rounded', size: ['sm', 'md', 'icon', 'icon-sm'], class: 'rounded-md' },
      { shape: 'rounded', size: ['lg', 'xl'], class: 'rounded-lg' },
      /** Figma master "Mark as paid" — the big primary CTA carries a drop shadow (= `shadow-lg`). */
      { variant: 'primary', size: 'xl', class: 'shadow-lg' },
    ],
    defaultVariants: { variant: 'primary', size: 'md', shape: 'rounded', fullWidth: false },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Render as the single child element (Radix Slot) instead of a <button>. */
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  shape,
  fullWidth,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, shape, fullWidth }), className)}
      {...props}
    />
  );
}
